import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/services/entities/auth.service';
import { HttpStatus } from '@core/enum/http-status.enum';
import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';

const REFRESH_EXCLUDED_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

let isRefreshTokenInProgress = false;
const refreshedAccessTokenSubject = new BehaviorSubject<string | null>(null);

const isRefreshExcludedUrl = (url: string): boolean =>
  REFRESH_EXCLUDED_PATHS.some((path) => url.includes(path));

const cloneRequestWithAccessToken = (
  request: HttpRequest<unknown>,
  accessToken: string,
): HttpRequest<unknown> =>
  request.clone({
    setHeaders: { Authorization: `Bearer ${accessToken}` },
  });

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status !== HttpStatus.UNAUTHORIZED ||
        isRefreshExcludedUrl(req.url)
      ) {
        return throwError(() => error);
      }
      return handleUnauthorizedRequest(req, next, authService);
    }),
  );
};

const handleUnauthorizedRequest = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
) => {
  if (isRefreshTokenInProgress) {
    return refreshedAccessTokenSubject.pipe(
      filter((accessToken): accessToken is string => accessToken !== null),
      take(1),
      switchMap((accessToken) =>
        next(cloneRequestWithAccessToken(request, accessToken)),
      ),
    );
  }

  isRefreshTokenInProgress = true;
  refreshedAccessTokenSubject.next(null);

  return authService.refresh().pipe(
    switchMap((response) => {
      isRefreshTokenInProgress = false;
      refreshedAccessTokenSubject.next(response.data.accessToken);
      return next(
        cloneRequestWithAccessToken(request, response.data.accessToken),
      );
    }),
    catchError((refreshError: HttpErrorResponse) => {
      isRefreshTokenInProgress = false;
      authService.logout();
      return throwError(() => refreshError);
    }),
  );
};
